import { getCollapse } from './collapse';
import { domReady } from './helpers';
import { getActiveModal, getModal } from './modal';

const CONTROL_SELECTOR = [
  '[data-ui-toggle="modal"]',
  '[data-ui-toggle="collapse"]',
  '[data-ui-dismiss="modal"]',
].join(',');

function isNativeInteractive(element) {
  return (
    element.matches('button, input, select, textarea, summary') ||
    (element.matches('a') && element.hasAttribute('href'))
  );
}

function findTarget(control) {
  const selector = control.getAttribute('data-ui-target');
  if (!selector) {
    return null;
  }

  try {
    return document.querySelector(selector);
  } catch {
    return null;
  }
}

function initializeControl(element) {
  if (!isNativeInteractive(element)) {
    if (!element.hasAttribute('role')) {
      element.setAttribute('role', 'button');
    }
    if (!element.hasAttribute('tabindex')) {
      element.setAttribute('tabindex', '0');
    }
  }

  if (element.matches('[data-ui-toggle="collapse"]')) {
    getCollapse(element);
  }

  if (element.matches('[data-ui-toggle="modal"]')) {
    const target = findTarget(element);
    element.setAttribute('aria-haspopup', 'dialog');
    if (target?.id && !element.hasAttribute('aria-controls')) {
      element.setAttribute('aria-controls', target.id);
    }
  }
}

function initializeControls(root = document) {
  root.querySelectorAll(CONTROL_SELECTOR).forEach(initializeControl);
}

function shouldPreventDismissNavigation(control) {
  if (!control.matches('a')) {
    return true;
  }

  const href = control.getAttribute('href');
  return !href || href === '#' || href === window.location.hash;
}

async function handleModalControl(control, event) {
  const target = findTarget(control);
  const nextModal = target ? getModal(target) : null;
  const currentModal = getActiveModal();

  event.preventDefault();

  if (nextModal) {
    await nextModal.show(control);
    return;
  }

  if (control.matches('[data-ui-dismiss="modal"]') && currentModal) {
    await currentModal.hide();
  }
}

function handleClick(event) {
  const control = event.target.closest(CONTROL_SELECTOR);
  if (!control) {
    return;
  }

  if (control.matches('[data-ui-toggle="modal"]')) {
    handleModalControl(control, event);
    return;
  }

  if (control.matches('[data-ui-dismiss="modal"]')) {
    if (shouldPreventDismissNavigation(control)) {
      event.preventDefault();
    }
    getActiveModal()?.hide();
    return;
  }

  if (control.matches('[data-ui-toggle="collapse"]')) {
    event.preventDefault();
    getCollapse(control)?.toggle(control);
  }
}

function handleKeydown(event) {
  if (event.key !== 'Enter' && event.key !== ' ') {
    return;
  }

  const control = event.target.closest(CONTROL_SELECTOR);
  if (!control || isNativeInteractive(control)) {
    return;
  }

  event.preventDefault();
  control.click();
}

domReady(() => {
  initializeControls();

  document.addEventListener('click', handleClick);
  document.addEventListener('keydown', handleKeydown);

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) {
          return;
        }

        if (node.matches(CONTROL_SELECTOR)) {
          initializeControl(node);
        }
        initializeControls(node);
      });
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });
});
